import * as React from 'react';
import type {ReactNode} from 'react';
import {Fragment, useContext, useRef} from 'react';
import {
  provider,
  assignProps,
  createView,
  createViewConfig,
  view,
} from '@sail/engine';
import type {ReactAriaTreeState as TreeState} from '@sail/react-aria';
import type {
  AriaLabelingProps,
  FocusStrategy,
  Key,
  LinkDOMProps,
} from '@react-types/shared';
import {
  useFocusRing,
  PressResponder,
  useMenuTriggerState,
  useMenu,
  useMenuItem,
  useMenuTrigger,
  useMenuSection,
} from '@sail/react-aria';
import {message, useIntl} from '@sail/intl';
import {tokens} from '@sail/tokens';
import type {Href} from '@sail/global-state';
import {Action} from './Action';

import type {View} from '../view';

import {
  useTreeState,
  useCollection,
  CollectionProvider,
  useItem,
  IndexedGroup,
  walkForKey,
} from '../internal/collections';
import type {
  Item as CollectionItem,
  ActionCallback,
} from '../internal/collections';
import {Popover} from './popover/Popover';
import {visuallyHidden} from '../utilStyles/visuallyHidden';
import {css} from '../css';
import {Divider} from './Divider';
import {Heading} from './Heading';
import {MenuConfig} from './MenuConfig';
import {CompositeOrientationContext} from './rovingTabIndex';

const dismissButtonLabel = message({
  id: '@sail/ui.menu.dismissButtonLabel',
  description: 'The aria-label for the dismiss buttons in the menu focus trap',
  defaultMessage: 'Dismiss',
});

export interface MenuProps extends AriaLabelingProps {
  /**
   * Handler that is called when an item is selected.
   */
  onAction?: (key: Key) => void;
  /**
   * Handler that is called when the menu closes.
   */
  onClose?: () => void;
  /**
   * Handler that is called when the menu opens/closes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The trigger Element to show/hide the menu. Must be a component that supports press events, such as a Button or Link.
   */
  trigger?: ReactNode;
  /** The contents of the menu. */
  children: ReactNode;

  /**
   * className to be applied to the containing div
   */
  className?: string;

  /**
   * When true, disabled items are skipped by keyboard focus and search functionality.
   * Defaults to false to preserve existing behavior.
   */
  disableFocusOnDisabledItems?: boolean;

  /**
   * Whether and where the menu should receive focus when it mounts.
   * Defaults to false for inline menus and first for trigger-backed menus.
   */
  autoFocus?: boolean | FocusStrategy;

  ref?: React.Ref<HTMLDivElement>;

  subviews?: View.Subviews<{
    popover: typeof Popover;
  }>;
}

export interface MenuGroupProps {
  /**
   * A heading grouping the menu items.
   */
  title?: ReactNode;
  /** Child items that belong to the group. */
  children: ReactNode;
  /**
   * An accessibility label for the group.
   */
  'aria-label'?: string;

  subviews?: View.Subviews<{
    separator: typeof Divider;
    heading: typeof Heading;
    list: 'ul';
  }>;
}

export interface MenuItemProps extends Omit<LinkDOMProps, 'href'> {
  /** Rendered contents of the item */
  children: ReactNode;
  /**
   * Handler that is called when an item is selected.
   */
  onAction?: (key: Key) => void;
  /**
   * The id of the item. This will be passed into the `onAction` handler of `Menu`
   */
  id?: string;
  /**
   * Marks an item as disabled. Disabled items cannot be selected, focused, or otherwise interacted with.
   */
  disabled?: boolean;
  /**
   * An accessibility label for this item.
   */
  'aria-label'?: string;
  /**
   * The content of the MenuItem as text. Useful if the MenuItem contains
   * nested children, but you still want it to be accessible via typeahead.
   */
  textValue?: string;
  /**
   * The type of the item
   */
  type?: 'default' | 'destructive';
  /**
   * Native `href` attribute that can be augmented by the `@sail/navigation` integration.
   */
  href?: Href;
  /**
   * Whether the menu should close when the menu item is selected.
   */
  closeOnSelect?: boolean;
}

interface MenuContextValue {
  id?: string;
  onClose?: () => void;
  state?: TreeState<unknown>;
  onAction?: ActionCallback;
}

const MenuStateContext = React.createContext<MenuContextValue>({});

function useMenuState(): MenuContextValue {
  return useContext(MenuStateContext);
}

export const MenuItemConfig = createViewConfig({
  name: 'MenuItem',
  props: {} as MenuItemProps,
  flattens: true,
  defaults: {
    disabled: false,
    closeOnSelect: true,
  },
});

const focus = css({focusRing: 'focus'});
const noOutline = css({focusRing: 'none'});
const downMobile =
  `@media (max-width: calc(${tokens['viewport.mobile']} - 0.1px)) and (any-pointer: coarse)` as 'down-mobile';
const upMobile =
  `@media (min-width: ${tokens['viewport.mobile']}), not all and (any-pointer: coarse)` as 'up-mobile';

export const MenuItem = MenuItemConfig.createView(
  ({onAction, disabled, id, children, textValue, closeOnSelect, ...props}) => {
    const {onClose, onAction: onMenuAction, state} = useMenuState();
    const itemData = React.useMemo(() => ({disabled}), [disabled]);
    const {key} = useItem({
      'aria-label': props['aria-label'] ?? '',
      typeaheadValue:
        typeof children === 'string'
          ? children
          : textValue ?? props['aria-label'] ?? '',
      id,
      data: itemData,
    });

    if (state == null) {
      throw new Error('<MenuItem> rendered outside <Menu>');
    }

    const ref = useRef<HTMLElement>(null);
    const {menuItemProps} = useMenuItem(
      {
        key,
        onAction: () => {
          onAction?.(key);
          onMenuAction?.(key);
        },
        onClose,
        isDisabled: disabled,
        closeOnSelect,
        'aria-label': props['aria-label'],
      },
      state,
      ref,
    );

    const {isFocusVisible, focusProps} = useFocusRing();
    const isLink = !!props.href;

    if (isLink) {
      return (
        <Action
          {...props}
          {...menuItemProps}
          {...focusProps}
          disabled={disabled}
          ref={ref as React.RefObject<HTMLAnchorElement>}
          uses={[
            isFocusVisible ? focus : noOutline,
            css({display: 'list-item'}),
            css({font: 'label.medium.emphasized'}),
          ]}
        >
          {children}
        </Action>
      );
    } else {
      return (
        <view.div
          {...props}
          {...menuItemProps}
          {...focusProps}
          ref={ref as React.RefObject<HTMLDivElement>}
          uses={[isFocusVisible ? focus : noOutline]}
        >
          {children}
        </view.div>
      );
    }
  },
  {
    css: {
      marginY: 'space.0',
      color: 'text',
      minWidth: '160px',
      borderRadius: `calc(${tokens['border.radius.medium']} - 1px - 4px)`, // 1px (Popover border width) - 4px (focus ring width).
      cursor: 'pointer',
      [upMobile]: {
        font: 'label.medium',
        paddingX: 'space.125',
        paddingY: 'space.75',
        marginX: 'space.50',
      },
      [downMobile]: {
        font: 'label.large.emphasized',
        fontSize: 'label.large',
        paddingX: 'small',
        paddingY: 'xsmall',
        marginX: 'space.150',
      },
    },
    forward: {disabled: true},
    variants: {
      type: {
        destructive: css({
          color: 'action.destructive',
        }),
      },
      disabled: {
        true: css({
          cursor: 'default',
          color: 'form.disabled',
        }),
        false: css({
          ':hover': {
            backgroundColor: 'offset',
            color: 'text',
          },
        }),
      },
    },
  },
);

export const MenuGroupConfig = createViewConfig({
  name: 'MenuGroup',
  props: {} as MenuGroupProps,
});

export const MenuGroup = MenuGroupConfig.createView(
  ({title, children, subviews, ...props}) => {
    const {key} = useItem({
      'aria-label': props['aria-label'] ?? '',
      type: 'section',
    });

    const {state} = useMenuState();

    const {itemProps, headingProps, groupProps} = useMenuSection({
      heading: title,
      'aria-label': props['aria-label'],
    });

    if (state == null) {
      throw new Error('<MenuGroup> rendered outside <Menu>');
    }

    return (
      <>
        {key !== state.collection.getFirstKey() ? (
          <view.li role="presentation">
            <Divider inherits={subviews.separator} />
          </view.li>
        ) : null}
        <view.li {...props} {...itemProps}>
          {title ? (
            <Heading as="h3" {...headingProps} inherits={subviews.heading}>
              {title}
            </Heading>
          ) : null}
          <view.ul
            inherits={subviews.list}
            {...groupProps}
            css={{
              listStyle: 'none',
              margin: 'space.0',
              padding: 'space.0',
              stack: 'y',
              [downMobile]: {
                gap: 'space.50',
              },
            }}
          >
            <IndexedGroup>{children}</IndexedGroup>
          </view.ul>
        </view.li>
      </>
    );
  },
  {
    subviews: {
      separator: {
        css: {
          [upMobile]: {
            marginY: 'space.50',
          },
          [downMobile]: {
            marginY: 'space.100',
          },
        },
      },
      heading: {
        css: {
          display: 'block',
          font: 'heading.xsmall',
          color: 'text',
          paddingY: 'space.75',
          [upMobile]: {
            marginX: 'space.50',
            marginY: 'space.0',
            paddingX: 'space.125',
          },
          [downMobile]: {
            marginX: 'space.150',
            marginTop: 'space.0',
            marginBottom: 'space.50',
            paddingX: 'small',
          },
        },
      },
    },
  },
);

interface MenuItems extends MenuProps, MenuContextValue {
  autoFocus: boolean | FocusStrategy;
}

const MenuItems = createView(
  ({
    children,
    className,
    inherits,
    onAction,
    disableFocusOnDisabledItems,
    autoFocus,
    ...props
  }: View.RenderProps<MenuItems>) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const didFocusRef = useRef<boolean>(false);
    const timeoutRef = useRef<number>();
    const collection = useCollection(children);
    const {onClose} = useMenuState();

    const disabledKeys = React.useMemo(() => {
      if (!disableFocusOnDisabledItems) {
        return undefined;
      }

      const items = collection.getItems();
      const keys = new Set<Key>();
      const walkItems = (entries: Array<CollectionItem<unknown>>) => {
        entries.forEach((item) => {
          if (
            (item.data as {disabled?: boolean} | undefined)?.disabled === true
          ) {
            keys.add(item.key);
          }
          if (item.childItems && item.childItems.length > 0) {
            walkItems(item.childItems as Array<CollectionItem<unknown>>);
          }
        });
      };

      walkItems(items as Array<CollectionItem<unknown>>);
      return keys;
    }, [collection, disableFocusOnDisabledItems]);

    const state = useTreeState({
      getItems: collection.getItems,
      disabledKeys,
    });

    const {menuProps} = useMenu(
      {
        ...props,
        // The collection is empty on the first render, so trigger-backed menus
        // initially focus the menu container before the delayed item autofocus.
        // Inline menus do not have an autofocus intent and must not preempt an
        // ancestor FocusScope (for example, a Dialog).
        autoFocus,
      },
      state,
      ref,
    );

    // this recreates `autoFocus` behavior in `useMenu`. because we do two-pass
    // rendering to get the collection children, the collection is empty the first
    // time we render the menu, and there's nothing to focus.
    React.useEffect(() => {
      timeoutRef.current && cancelAnimationFrame(timeoutRef.current);

      if (!autoFocus) {
        return;
      }

      const collectionFocusKey = state.collection.getFirstKey();

      // Give browser time to focus the next element
      timeoutRef.current = requestAnimationFrame(() => {
        if (collectionFocusKey) {
          const focusKey = walkForKey(collectionFocusKey, state);

          if (didFocusRef.current === false && typeof focusKey === 'string') {
            state.selectionManager.setFocusedKey(focusKey);
            state.selectionManager.setFocused(true);
            didFocusRef.current = true;
          }
        }
      });
    }, [autoFocus, state]);

    return (
      <CollectionProvider collection={collection}>
        <view.div
          className={className}
          inherits={inherits}
          ref={ref}
          {...menuProps}
          uses={[
            provider(MenuStateContext, {
              state,
              onClose,
              onAction,
            }),
          ]}
        >
          {collection.children}
        </view.div>
      </CollectionProvider>
    );
  },
);

export const Menu = MenuConfig.createView(
  ({subviews, autoFocus, ...props}) => {
    const {formatMessage} = useIntl();
    const triggerRef = useRef<HTMLElement>(null);
    const compositeOrientation = useContext(CompositeOrientationContext);
    const state = useMenuTriggerState({
      onOpenChange: (isOpen) => {
        props.onOpenChange?.(isOpen);
        !isOpen && props.onClose?.();
      },
    });
    const {menuTriggerProps, menuProps} = useMenuTrigger(
      {orientation: compositeOrientation},
      state,
      triggerRef,
    );
    const {autoFocus: _, ...menuPropsWithoutAutoFocus} = menuProps;
    const onClose = state.close;

    // if there is no trigger element, fall back to rendering the menu inline in an open state
    if (!props.trigger) {
      return <MenuItems {...props} autoFocus={autoFocus ?? false} />;
    }

    // if there *is* a trigger, render the menu relative to the trigger (via a popover)
    return (
      <Fragment>
        <PressResponder
          // trigger props contain aria-{expanded|haspopup|controls|pressed}
          {...menuTriggerProps}
          ref={triggerRef}
          isPressed={state.isOpen}
        >
          {props.trigger}
        </PressResponder>
        <MenuStateContext.Provider value={{onClose}}>
          <Popover
            targetRef={triggerRef}
            onClose={onClose}
            shouldCloseOnBlur
            inherits={subviews.popover}
            open={state.isOpen}
            bottomSheetTitle={props['aria-label']}
          >
            <view.button
              type="button"
              tabIndex={-1} // This dismiss button is not focusable by keyboard, but it is by accessibility tools when using arrow keys to navigate the menu
              aria-label={formatMessage(dismissButtonLabel)}
              onClick={onClose}
              uses={[visuallyHidden]}
            />
            <MenuItems
              {...props}
              autoFocus={autoFocus ?? 'first'}
              uses={[assignProps(menuPropsWithoutAutoFocus)]}
            >
              {props.children}
            </MenuItems>
            <view.button
              type="button"
              tabIndex={-1} // This dismiss button is not focusable by keyboard, but it is by accessibility tools when using arrow keys to navigate the menu
              aria-label={formatMessage(dismissButtonLabel)}
              onClick={onClose}
              uses={[visuallyHidden]}
            />
          </Popover>
        </MenuStateContext.Provider>
      </Fragment>
    );
  },
  {
    css: {
      margin: 'space.0',
      listStyle: 'none',
      overflowY: 'auto',
      userSelect: 'none',
      paddingX: 'space.0',
      paddingY: 'xsmall',
      height: 'fill',
      focusRing: 'none',
      [downMobile]: {
        stack: 'y',
        gap: 'space.50',
        paddingY: 'medium',
      },
    },
  },
);
